(() => {
  if (customElements.get('kr-extra-charm-editor')) return;
  const sameProperties = (a = {}, b = {}) => {
    const keys = Object.keys(a);
    return keys.length === Object.keys(b).length && keys.every(key => a[key] === b[key]);
  };
  customElements.define('kr-extra-charm-editor', class extends HTMLElement {
    connectedCallback() {
      this.onClick ??= event => {
        const button = event.target.closest?.('button');
        if (button?.closest('kr-extra-charm-editor') !== this) return;
        event.preventDefault();
        if (button.hasAttribute('data-edit')) this.open();
        else if (button.hasAttribute('data-close') && !this.pending) this.dialog?.close();
        else if (button.hasAttribute('data-extra-variant') && !this.pending && button.dataset.available === 'true') {
          this.selected = button.dataset.extraVariant;
          this.paint();
        } else if (button.hasAttribute('data-save')) this.save();
      };
      this.onCancel ??= event => {if (this.pending) event.preventDefault();};
      this.onClose ??= () => this.querySelector('[data-edit]')?.focus({preventScroll: true});
      this.addEventListener('click', this.onClick);
      this.addEventListener('cancel', this.onCancel, true);
      this.addEventListener('close', this.onClose, true);
    }
    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('cancel', this.onCancel, true);
      this.removeEventListener('close', this.onClose, true);
      if (this.dialog?.open) this.dialog.close();
    }
    open() {
      if (this.pending) return;
      try {
        this.dialog = this.querySelector('dialog');
        this.status = this.querySelector('[data-status]');
        this.config = JSON.parse(this.querySelector('[data-config]').textContent);
        this.choices = [...this.querySelectorAll('[data-extra-variant]')];
        this.selected = String(this.config.variant);
        if (!this.dialog?.showModal || !this.config.key || !this.config.parentKey || !this.choices.some(button => button.dataset.extraVariant === this.selected)) throw Error('config');
        this.querySelector('[data-error]').hidden = true;
        this.status.textContent = this.needsReview ? this.config.labels.review : '';
        this.paint();
        this.dialog.showModal();
        this.querySelector('[aria-pressed="true"]')?.focus();
      } catch (_) {this.querySelector('[data-error]').hidden = false;}
    }
    paint() {
      const selected = this.choices.find(button => button.dataset.extraVariant === this.selected);
      this.querySelector('[data-selection]').textContent = selected?.dataset.name || '';
      this.querySelector('[data-quote]').textContent = selected?.dataset.price || '';
      this.choices.forEach(button => {
        button.setAttribute('aria-pressed', String(button === selected));
        button.disabled = Boolean(this.pending || this.needsReview || button.dataset.available !== 'true');
      });
      this.querySelector('[data-save]').disabled = Boolean(this.pending || this.needsReview || this.selected === String(this.config.variant));
      this.querySelector('[data-close]').disabled = Boolean(this.pending);
      this.querySelector('[data-review]').hidden = !this.needsReview;
    }
    async request(url, body) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 12000);
      try {
        const response = await fetch(url, {credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
          headers: {Accept: 'application/json', ...(body ? {'Content-Type': 'application/json'} : {})},
          ...(body ? {method: 'POST', body: JSON.stringify(body)} : {})});
        if (!response.ok) throw Error('request');
        return await response.json();
      } finally {clearTimeout(timer);}
    }
    async save() {
      if (this.pending || this.needsReview || this.selected === String(this.config.variant)) return;
      const choice = this.choices.find(button => button.dataset.extraVariant === this.selected && button.dataset.available === 'true');
      if (!choice) return;
      this.pending = true;
      this.status.textContent = this.config.labels.saving;
      this.paint();
      let mutationStarted = false, before, old, parent, properties, expected, targetBefore;
      const parentOf = (cart, source) => {
        const matches = cart.items.filter(item => item.id === source.id && sameProperties(item.properties, source.properties));
        return matches.length === 1 ? matches[0] : null;
      };
      const targets = (cart, parentKey) => cart.items.filter(item => item.id === Number(this.selected) && item.parent_relationship?.parent_key === parentKey && sameProperties(item.properties, properties));
      const originals = (cart, parentKey) => cart.items.filter(item => item.id === old.id && item.parent_relationship?.parent_key === parentKey && sameProperties(item.properties, old.properties));
      const quantity = lines => lines.reduce((sum, item) => sum + item.quantity, 0);
      let root, cartPage;
      try {
        root = new URL(this.dataset.root, location.origin);
        cartPage = new URL(this.dataset.cartUrl, location.origin);
        if (root.origin !== location.origin || cartPage.origin !== location.origin) throw Error('origin');
        before = await this.request(new URL('cart.js', root));
        old = before.items.find(item => item.key === this.config.key && item.product_id === this.config.productId && item.id === this.config.variant);
        parent = before.items.find(item => item.key === old?.parent_relationship?.parent_key);
        if (!old || !parent || parent.key !== this.config.parentKey || old.quantity !== this.config.quantity) {
          this.status.textContent = this.config.labels.stale;
          this.needsReview = true;
          return;
        }
        properties = {...old.properties, Style: choice.dataset.name};
        targetBefore = quantity(targets(before, parent.key));
        expected = targetBefore + old.quantity;
        mutationStarted = true;
        await this.request(new URL('cart/add.js', root), {items: [{id: Number(this.selected), quantity: old.quantity, parent_line_key: parent.key, properties}]});
        const added = await this.request(new URL('cart.js', root));
        const currentParent = parentOf(added, parent);
        const source = currentParent && originals(added, currentParent.key);
        if (!currentParent || source.length !== 1 || source[0].quantity !== old.quantity || quantity(targets(added, currentParent.key)) !== expected) throw Error('unconfirmed');
        await this.request(new URL('cart/change.js', root), {id: source[0].key, quantity: 0});
        const after = await this.request(new URL('cart.js', root));
        const finalParent = parentOf(after, parent);
        if (!finalParent || finalParent.quantity !== parent.quantity || originals(after, finalParent.key).length || quantity(targets(after, finalParent.key)) !== expected) throw Error('unconfirmed');
        location.assign(cartPage.href);
      } catch (_) {
        let completed = false;
        if (mutationStarted) {
          try {
            const latest = await this.request(new URL('cart.js', root));
            const currentParent = parentOf(latest, parent);
            const sources = currentParent && originals(latest, currentParent.key);
            const replacements = currentParent && targets(latest, currentParent.key);
            if (currentParent && currentParent.quantity === parent.quantity && sources.length === 0 && quantity(replacements) === expected) completed = true;
            else if (currentParent && sources.length === 1 && sources[0].quantity === old.quantity && replacements.length === 1 && quantity(replacements) === expected) {
              await this.request(new URL('cart/change.js', root), {id: replacements[0].key, quantity: targetBefore});
            }
          } catch (_) {}
          this.needsReview = true;
        }
        if (completed) location.assign(cartPage.href);
        else this.status.textContent = mutationStarted ? this.config.labels.review : this.config.labels.error;
      } finally {
        this.pending = false;
        this.paint();
      }
    }
  });
})();
